import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "recipientInput",
    "recipientResults",
    "resultsContent",
    "loading",
    "selectedRecipients",
    "messageInput",
    "sendButton",
    "attachmentInput",
    "attachmentLabel",
    "attachmentList",
    "userSearchSection",
    "officeEmailSection",
    "officeEmailInput",
    "officeNameInput"
  ]

  connect() {
    this.selectedRecipientIds = []
    this.searchTimeout = null
    this.isSubmitting = false
    this.isOfficeEmailMode = false
    this.originalPlaceholder = this.recipientInputTarget.placeholder

    // Prevent double submission
    this.element.querySelector('form')?.addEventListener('submit', this.handleSubmit.bind(this))

    // Check for prefill data
    this.checkPrefill()
  }

  checkPrefill() {
    const modal = document.getElementById('new-conversation-modal')

    // Check for email prefill (claimable/unclaimed org from network)
    const prefillEmail = modal?.dataset.prefillEmail
    const prefillName = modal?.dataset.prefillName

    if (prefillEmail && prefillEmail !== '') {
      modal.dataset.prefillEmail = ''
      modal.dataset.prefillName = ''

      this.switchToOfficeEmail()
      this.officeEmailInputTarget.value = prefillEmail
      if (prefillName) {
        this.officeNameInputTarget.value = prefillName
      }
      this.updateSendButton()
      return
    }

    // Check for org prefill (claimed org from network)
    this.checkPrefillOrg()
  }

  async checkPrefillOrg() {
    const modal = document.getElementById('new-conversation-modal')
    const prefillOrgId = modal?.dataset.prefillOrgId

    if (prefillOrgId && prefillOrgId !== '') {
      // Clear the prefill so it doesn't trigger again
      modal.dataset.prefillOrgId = ''

      try {
        const response = await fetch(`/conversations/search_recipients?org_id=${prefillOrgId}`, {
          headers: {
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        })

        if (!response.ok) throw new Error("Failed to fetch org users")

        const data = await response.json()
        const org = data.organizations?.[0]

        if (org && org.users) {
          org.users.forEach(user => {
            if (!this.selectedRecipientIds.includes(user.id)) {
              this.selectedRecipientIds.push(user.id)
              this.addRecipientTag(user.id, user.name)
            }
          })
          this.updateSendButton()
        }
      } catch (error) {
        console.error("Error prefilling org users:", error)
      }
    }
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // --- Mode Switching ---

  switchToOfficeEmail() {
    this.isOfficeEmailMode = true
    this.userSearchSectionTarget.classList.add("d-none")
    this.officeEmailSectionTarget.classList.remove("d-none")

    // Clear user search state
    this.selectedRecipientIds = []
    this.selectedRecipientsTarget.innerHTML = ""
    this.recipientInputTarget.value = ""
    this.hideResults()

    this.updateSendButton()
    this.officeEmailInputTarget.focus()
  }

  switchToUserSearch() {
    this.isOfficeEmailMode = false
    this.officeEmailSectionTarget.classList.add("d-none")
    this.userSearchSectionTarget.classList.remove("d-none")

    // Clear office email state
    this.officeEmailInputTarget.value = ""
    this.officeNameInputTarget.value = ""

    this.updateSendButton()
    this.recipientInputTarget.focus()
  }

  // --- Form Submission ---

  handleSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return false
    }

    // Validate email format in office email mode
    if (this.isOfficeEmailMode) {
      const email = this.officeEmailInputTarget.value.trim()
      if (!this.isValidEmail(email)) {
        event.preventDefault()
        alert("Please enter a valid email address")
        return false
      }
    }

    this.isSubmitting = true
    this.sendButtonTarget.disabled = true
    this.sendButtonTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status"></span>Sending...'
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  focusRecipientInput() {
    this.recipientInputTarget.focus()
  }

  // --- Recipient Search ---

  searchRecipients(event) {
    const query = event.target.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    this.showLoading()
    this.showResults()

    try {
      const response = await fetch(`/conversations/search_recipients?query=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const data = await response.json()
      this.renderResults(data)
    } catch (error) {
      console.error("Search error:", error)
      this.resultsContentTarget.innerHTML = '<div class="p-3 text-muted text-center">Error searching recipients</div>'
    } finally {
      this.hideLoading()
    }
  }

  renderResults(data) {
    const { organizations } = data
    let html = ""

    if (!organizations || organizations.length === 0) {
      html = '<div class="p-3 text-muted text-center">No results found</div>'
    } else {
      organizations.forEach((org, orgIndex) => {
        // Organization header
        html += `<div class="recipient-org-header">${this.escapeHtml(org.name)}</div>`

        // "All X users at Org" option (only if org has an id and more than 1 user)
        if (org.id && org.total_users > 1) {
          html += `
            <div class="recipient-result-item recipient-all-users"
                 data-action="click->new-conversation#selectAllOrgUsers"
                 data-org-id="${org.id}"
                 data-org-name="${this.escapeHtml(org.name)}"
                 data-user-count="${org.total_users}">
              <div class="recipient-avatar all-users-avatar">
                <i class="fa-solid fa-users"></i>
              </div>
              <span class="recipient-name">All ${org.total_users} users at ${this.escapeHtml(org.name)}</span>
            </div>
          `
        }

        // Individual users
        org.users.forEach(user => {
          if (!this.selectedRecipientIds.includes(user.id)) {
            const avatarColor = this.getAvatarColor(user.id, user.name)
            html += `
              <div class="recipient-result-item"
                   data-action="click->new-conversation#selectRecipient"
                   data-id="${user.id}"
                   data-name="${this.escapeHtml(user.name)}"
                   data-type="user">
                <div class="recipient-avatar" style="background-color: ${avatarColor}">
                  ${user.avatar_url
                    ? `<img src="${user.avatar_url}" alt="${this.escapeHtml(user.name)}" class="avatar-image">`
                    : this.getInitials(user.name)
                  }
                </div>
                <span class="recipient-name">${this.escapeHtml(user.name)}</span>
              </div>
            `
          }
        })

        // "Show more users" link if there are more
        if (org.has_more && org.id) {
          html += `
            <div class="recipient-show-more"
                 data-action="click->new-conversation#showMoreUsers"
                 data-org-id="${org.id}">
              <div class="show-more-spacer"></div>
              <span>Show more users</span>
            </div>
          `
        }

        // Add separator between organizations (except last one)
        if (orgIndex < organizations.length - 1) {
          html += `<div class="recipient-org-separator"></div>`
        }
      })
    }

    this.resultsContentTarget.innerHTML = html
  }

  async selectAllOrgUsers(event) {
    const item = event.currentTarget
    const orgId = item.dataset.orgId
    const orgName = item.dataset.orgName

    try {
      // Fetch all users from this organization
      const response = await fetch(`/conversations/search_recipients?query=&org_id=${orgId}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to fetch org users")

      const data = await response.json()
      const org = data.organizations?.find(o => o.id === parseInt(orgId))

      if (org && org.users) {
        org.users.forEach(user => {
          if (!this.selectedRecipientIds.includes(user.id)) {
            this.selectedRecipientIds.push(user.id)
            this.addRecipientTag(user.id, user.name)
          }
        })
      }
    } catch (error) {
      console.error("Error selecting org users:", error)
      // Fallback - just add a tag showing org name
      alert(`Could not load all users from ${orgName}. Please select users individually.`)
    }

    this.recipientInputTarget.value = ""
    this.hideResults()
    this.updateSendButton()
    this.recipientInputTarget.focus()
  }

  async showMoreUsers(event) {
    const orgId = event.currentTarget.dataset.orgId

    try {
      const response = await fetch(`/conversations/search_recipients?query=&org_id=${orgId}&show_all=true`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to fetch more users")

      const data = await response.json()
      this.renderResults(data)
    } catch (error) {
      console.error("Error loading more users:", error)
    }
  }

  selectRecipient(event) {
    const item = event.currentTarget
    const id = parseInt(item.dataset.id)
    const name = item.dataset.name

    if (!this.selectedRecipientIds.includes(id)) {
      this.selectedRecipientIds.push(id)
      this.addRecipientTag(id, name)
    }

    this.recipientInputTarget.value = ""
    this.hideResults()
    this.updateSendButton()
    this.recipientInputTarget.focus()
  }

  addRecipientTag(id, name) {
    const tag = document.createElement("div")
    tag.className = "recipient-tag"
    tag.dataset.id = id
    tag.innerHTML = `
      <span>${this.escapeHtml(name)}</span>
      <input type="hidden" name="recipient_ids[]" value="${id}">
      <button type="button" class="btn-remove-recipient" data-action="click->new-conversation#removeRecipient">
        <i class="fa-solid fa-times"></i>
      </button>
    `
    this.selectedRecipientsTarget.appendChild(tag)
    this.updatePlaceholder()
  }

  removeRecipient(event) {
    event.stopPropagation()
    const tag = event.currentTarget.closest(".recipient-tag")
    const id = parseInt(tag.dataset.id)

    this.selectedRecipientIds = this.selectedRecipientIds.filter(rid => rid !== id)
    tag.remove()
    this.updatePlaceholder()
    this.updateSendButton()
  }

  updatePlaceholder() {
    if (this.selectedRecipientIds.length > 0) {
      this.recipientInputTarget.placeholder = ""
    } else {
      this.recipientInputTarget.placeholder = this.originalPlaceholder
    }
  }

  showResults() {
    this.recipientResultsTarget.classList.remove("d-none")
  }

  hideResults() {
    this.recipientResultsTarget.classList.add("d-none")
  }

  showLoading() {
    this.loadingTarget.classList.remove("d-none")
    this.resultsContentTarget.innerHTML = ""
  }

  hideLoading() {
    this.loadingTarget.classList.add("d-none")
  }

  // --- Attachments ---

  // Attachments are direct-uploaded to S3 by the file-upload controller on the
  // form. It renders a .file-wrapper progress row per file inside the
  // attachment list and fills hidden file_upload[<key>][file] fields with blob
  // signed_ids on completion.
  attachmentsChanged() {
    const count = this.attachmentListTarget.querySelectorAll(".file-wrapper").length
    this.attachmentLabelTarget.textContent = count === 0
      ? "No attachments"
      : `${count} attachment${count !== 1 ? 's' : ''}`
    this.updateSendButton()
  }

  hasCompletedAttachments() {
    return this.attachmentListTarget.querySelectorAll(".file-wrapper--completed").length > 0
  }

  hasPendingUploads() {
    return this.attachmentListTarget.querySelector(".file-wrapper--uploading") !== null
  }

  // --- Send Button ---

  updateSendButton() {
    const hasMessage = this.messageInputTarget.value.trim().length > 0
    const hasContent = hasMessage || this.hasCompletedAttachments()

    let hasRecipient = false
    if (this.isOfficeEmailMode) {
      hasRecipient = this.officeEmailInputTarget.value.trim().length > 0
    } else {
      hasRecipient = this.selectedRecipientIds.length > 0
    }

    this.sendButtonTarget.disabled = !(hasRecipient && hasContent) || this.hasPendingUploads()
  }

  // --- Utilities ---

  getInitials(name) {
    // Remove "Dr." prefix if present
    let cleanName = name.replace(/^Dr\.?\s*/i, '').trim()

    const parts = cleanName.split(" ")
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return cleanName.substring(0, 2).toUpperCase()
  }

  // Get a consistent color based on user ID or name
  getAvatarColor(userId, name) {
    const colors = [
      '#4F46E5', '#7C3AED', '#DB2777', '#DC2626', '#EA580C',
      '#CA8A04', '#16A34A', '#0D9488', '#0284C7', '#6366F1'
    ]
    // Use user ID if available, otherwise hash the name
    const index = userId ? userId % colors.length : name.length % colors.length
    return colors[index]
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
